import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Zap, DollarSign, Crown, Clock, TrendingUp, Check, ArrowRight } from "lucide-react";

const handleLogin = () => {
  const redirectUrl = window.location.origin + "/affiliate";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function AffiliateLanding() {
  const canonical = typeof window !== "undefined" ? window.location.href : "";
  const perks = [
    { icon: DollarSign, title: "20% recurring", body: "Earn 20% of every paying referral's plan — every single month they stay." },
    { icon: Crown, title: "VIP 30% tier", body: "Hit 10 active paying subscribers and auto-unlock 30% recurring commission." },
    { icon: Clock, title: "30-day hold", body: "A short hold covers refunds/chargebacks, then earnings clear for payout." },
    { icon: TrendingUp, title: "Real-time dashboard", body: "Track clicks, trials, paid subs and earnings live from your Affiliate tab." },
  ];
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="affiliate-landing">
      <Helmet>
        <title>Affiliate Program — Earn 20-30% recurring | AutoGridSocial</title>
        <meta name="description" content="Join the AutoGridSocial affiliate program. Earn 20% recurring commission (30% VIP) on every paying referral. Real-time tracking, PayPal payouts." />
        <link rel="canonical" href={canonical} />
      </Helmet>
      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" aria-label="AutoGridSocial home">
            <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center"><Zap className="h-5 w-5 text-white" /></div>
            <span className="font-display font-bold text-lg tracking-tight">AutoGridSocial</span>
          </Link>
          <Button onClick={handleLogin} data-testid="aff-landing-join" className="bg-primary hover:bg-primary/90 text-white text-sm">Become an affiliate</Button>
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden border-b border-border/60" aria-label="Affiliate program overview">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[420px] w-[820px] rounded-full bg-primary/10 blur-[120px] pointer-events-none" />
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16 text-center">
            <Badge className="mb-5 bg-secondary text-foreground border-border">Partner program</Badge>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mb-5">Get paid to share AutoGridSocial.</h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Earn <span className="text-foreground font-medium">20% recurring commission</span> (30% as a VIP) on every paying referral. Real-time tracking, a 30-day cookie window, and PayPal payouts.
            </p>
            <Button onClick={handleLogin} size="lg" data-testid="aff-landing-cta" className="bg-primary hover:bg-primary/90 text-white h-12 px-8 group">
              Join free — get your link <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16" aria-label="Program perks">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {perks.map((p) => (
              <div key={p.title} className="rounded-xl border border-border bg-card p-6 card-hover">
                <div className="h-10 w-10 rounded-lg bg-primary/15 flex items-center justify-center mb-3"><p.icon className="h-5 w-5 text-primary" /></div>
                <h3 className="font-semibold mb-1">{p.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="border-t border-border/60 bg-card/30" aria-label="How it works">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <h2 className="text-2xl font-display font-bold text-center mb-8">How it works</h2>
            <ol className="space-y-4">
              {["Sign in and grab your unique referral link (with a custom name if you like).",
                "Share it — anyone who signs up in the next 30 days is attributed to you.",
                "Earn 20% (30% VIP) every month they keep paying. Cleared earnings pay out via PayPal once you pass $50."].map((t, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="h-6 w-6 rounded-full bg-primary/15 text-primary text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                  <span className="text-sm text-muted-foreground">{t}</span>
                </li>
              ))}
            </ol>
            <div className="text-center mt-10">
              <Button onClick={handleLogin} size="lg" className="bg-primary hover:bg-primary/90 text-white h-12 px-8">Become an affiliate</Button>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
